import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Form, Row, Col, Alert, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faSort, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { getOrders } from '../services/api';
import '../styles/InfiniteTable.css';
import './Orders.css';
import PageHeader from './PageHeader';

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'transDate', direction: 'desc' });
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, [startDate, endDate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getOrders(startDate, endDate);
      let ordersData = Array.isArray(data) ? data : [];
      
      // Apply sorting
      if (sortConfig.key) {
        ordersData = [...ordersData].sort((a, b) => {
          let aValue = a[sortConfig.key];
          let bValue = b[sortConfig.key];
          
          // Handle null/undefined values
          if (aValue === null || aValue === undefined) aValue = '';
          if (bValue === null || bValue === undefined) bValue = '';
          
          // Handle dates
          if (sortConfig.key === 'transDate') {
            aValue = new Date(aValue || 0);
            bValue = new Date(bValue || 0);
          }
          
          if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        });
      }
      
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    fetchOrders(); // Refetch with new sort
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return faSort;
    }
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  return (
    <div className="orders-container">
      <PageHeader 
        title="Order Management" 
        icon={faClipboardList}
        subtitle="View and manage customer orders"
      />
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <Form.Control
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col xs="auto">
            <Form.Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              {/* Add your status options here */}
            </Form.Select>
          </Col>
        </Row>
        <div className="table-wrapper">
          <div className="table-scroll">
            <Table className="infinite-table">
              <thead>
                <tr>
                  <th 
                    onClick={() => handleSort('idTrans')} 
                    data-align="right"
                  >
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <span>Order #</span>
                      <FontAwesomeIcon 
                        icon={getSortIcon('idTrans')} 
                        className="ms-2"
                      />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('transDate')} 
                    data-align="right"
                  >
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <span>Date</span>
                      <FontAwesomeIcon 
                        icon={getSortIcon('transDate')} 
                        className="ms-2"
                      />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('typeName')} 
                    data-align="left"
                  >
                    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                      <span>Type</span>
                      <FontAwesomeIcon 
                        icon={getSortIcon('typeName')} 
                        className="ms-2"
                      />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('CustomerRef')} 
                    data-align="left"
                  >
                    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                      <span>Reference</span>
                      <FontAwesomeIcon 
                        icon={getSortIcon('CustomerRef')} 
                        className="ms-2"
                      />
                    </div>
                  </th>
                  <th 
                    data-align="center"
                  >
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <span>Actions</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.idTrans}>
                    <td style={{ textAlign: 'right', padding: '6px 12px', fontSize: '0.85rem', lineHeight: '1.2' }}>
                      {order.idTrans}
                    </td>
                    <td style={{ textAlign: 'right', padding: '6px 12px', fontSize: '0.85rem', lineHeight: '1.2' }}>
                      {new Date(order.transDate).toLocaleDateString()}
                    </td>
                    <td style={{ textAlign: 'left', padding: '6px 12px', fontSize: '0.85rem', lineHeight: '1.2' }}>
                      {order.typeName || 'Unknown'}
                    </td>
                    <td style={{ textAlign: 'left', padding: '6px 12px', fontSize: '0.85rem', lineHeight: '1.2' }}>
                      {order.CustomerRef}
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px 12px', fontSize: '0.85rem', lineHeight: '1.2' }}>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigate(`/orders/${order.idTrans}`)}
                        style={{ 
                          padding: '2px 8px',
                          fontSize: '0.75rem',
                          lineHeight: '1.2',
                          height: 'auto'
                        }}
                      >
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default Orders;
